'use client';

/**
 * Firebase web SDK initialiser. Lazy + idempotent so importing this module on
 * the server (RSC) does nothing — the actual `initializeApp` call only runs
 * inside `getFirebaseApp()` from the browser.
 *
 * All config values come from `NEXT_PUBLIC_FIREBASE_*` env vars so they can be
 * inlined at build time. The VAPID key is used only for web push.
 */

import { initializeApp, getApps, type FirebaseApp } from 'firebase/app';
import type { Analytics } from 'firebase/analytics';

export const FIREBASE_PUBLIC_CONFIG = {
  apiKey: process.env.NEXT_PUBLIC_FIREBASE_API_KEY,
  authDomain: process.env.NEXT_PUBLIC_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.NEXT_PUBLIC_FIREBASE_PROJECT_ID,
  storageBucket: process.env.NEXT_PUBLIC_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.NEXT_PUBLIC_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.NEXT_PUBLIC_FIREBASE_APP_ID,
  measurementId: process.env.NEXT_PUBLIC_FIREBASE_MEASUREMENT_ID,
};

export const FIREBASE_VAPID_KEY = process.env.NEXT_PUBLIC_FIREBASE_VAPID_KEY;

let _app: FirebaseApp | null = null;
let _analytics: Analytics | null = null;
let _analyticsInitTried = false;

export function isFirebaseConfigured(): boolean {
  return Boolean(
    FIREBASE_PUBLIC_CONFIG.apiKey &&
      FIREBASE_PUBLIC_CONFIG.projectId &&
      FIREBASE_PUBLIC_CONFIG.messagingSenderId &&
      FIREBASE_PUBLIC_CONFIG.appId
  );
}

export function getFirebaseApp(): FirebaseApp | null {
  if (typeof window === 'undefined') return null;
  if (!isFirebaseConfigured()) {
    if (process.env.NODE_ENV !== 'production') {
      console.warn('[firebase] NEXT_PUBLIC_FIREBASE_* env vars missing — push disabled.');
    }
    return null;
  }
  if (_app) return _app;
  const existing = getApps()[0];
  _app = existing ?? initializeApp(FIREBASE_PUBLIC_CONFIG as Record<string, string>);
  return _app;
}

/**
 * Lazy Firebase Analytics getter. Browser-only and guarded by `isSupported()`
 * so it no-ops in SSR / unsupported environments (Capacitor native webview,
 * private-mode Safari, etc.). Requires `NEXT_PUBLIC_FIREBASE_MEASUREMENT_ID`.
 *
 * Returns a Promise so the dynamic `firebase/analytics` import doesn't bloat
 * the initial bundle. Safe to call multiple times.
 */
export async function getFirebaseAnalytics(): Promise<Analytics | null> {
  if (typeof window === 'undefined') return null;
  if (_analytics) return _analytics;
  if (_analyticsInitTried) return _analytics;
  _analyticsInitTried = true;

  const app = getFirebaseApp();
  if (!app) return null;
  if (!FIREBASE_PUBLIC_CONFIG.measurementId) {
    if (process.env.NODE_ENV !== 'production') {
      console.warn('[firebase] NEXT_PUBLIC_FIREBASE_MEASUREMENT_ID missing — analytics disabled.');
    }
    return null;
  }

  try {
    const { getAnalytics, isSupported } = await import('firebase/analytics');
    if (!(await isSupported())) return null;
    _analytics = getAnalytics(app);
    return _analytics;
  } catch (e) {
    console.error('[firebase] analytics init failed', e);
    return null;
  }
}
